/**
 * ODP — OpenDocument presentation (§8 Phase 3, §17).
 *
 * The model is a {@link PptxModel}, so the slide stage, thumbnail rail, notes
 * pane, outline and per-shape text editing all apply to an ODP unchanged. §17's
 * rule that animations and transitions are *preserved but not played* holds by
 * construction: the shared slide viewer has no animation code, and the ODF save
 * path copies `presentation:animations` through untouched.
 *
 * As with the other ODF families, only the paragraphs of an edited text box are
 * rewritten; master pages, graphic styles, media and the animation timeline are
 * copied verbatim (§13, §24).
 */
import { FORMATS_BY_ID, formatFileSize } from '@moduviewer/core';
import type {
  ExportResult, FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview, ValidationReport,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';
import { hasPart, readPart, repackPackage } from '../ooxml/package.js';
import type { PptxImage, PptxModel, PptxSlide } from '../pptx/handler.js';
import { CONTENT_PART, ODF_MIME, checkMimetypeEntry, imageContentType, openOdf } from './package.js';
import type { OdfDocument } from './package.js';
import { parseStyles } from './styles.js';
import { applyOdpShapeEdits, parseOdp, readOdpPageSize } from './presentation-xml.js';
import type { OdfSlide } from './presentation-xml.js';

const PREVIEW_SHAPES = 60;

/* ------------------------------------------------------------------ *
 * reading
 * ------------------------------------------------------------------ */

interface Read {
  readonly doc: OdfDocument;
  readonly slides: readonly OdfSlide[];
  readonly width: number;
  readonly height: number;
  readonly imageParts: readonly string[];
  readonly externalRefs: readonly string[];
  readonly truncated: boolean;
}

function read(bytes: Uint8Array, maxShapes: number, signal?: AbortSignal): Read {
  const doc = openOdf(bytes);
  if (signal) throwIfAborted(signal);
  const styles = parseStyles(doc.stylesXml, doc.contentXml);
  const size = readOdpPageSize(doc.stylesXml);
  const parsed = parseOdp(doc.contentXml, { styles, maxShapes, width: size.width, height: size.height });
  return { doc, ...parsed };
}

/** Convert the reader's slides into the shared PptxSlide shape. */
function toPptxSlides(slides: readonly OdfSlide[]): PptxSlide[] {
  return slides.map((slide) => ({
    index: slide.index,
    part: slide.part,
    shapes: slide.shapes,
    notes: slide.notes,
    title: slide.title,
    hidden: slide.hidden,
  }));
}

function readImages(
  doc: OdfDocument, parts: readonly string[], budgetBytes: number,
): { images: Map<string, PptxImage>; skipped: string[] } {
  const images = new Map<string, PptxImage>();
  const skipped: string[] = [];
  let spent = 0;

  for (const part of new Set(parts)) {
    const contentType = imageContentType(doc, part);
    if (!contentType || !hasPart(doc.pkg, part)) { skipped.push(part); continue; }
    try {
      const bytes = readPart(doc.pkg, part);
      if (spent + bytes.length > budgetBytes) { skipped.push(part); continue; }
      spent += bytes.length;
      images.set(part, { part, bytes, contentType });
    } catch {
      skipped.push(part);
    }
  }
  return { images, skipped };
}

/* ------------------------------------------------------------------ *
 * handler
 * ------------------------------------------------------------------ */

export const odpHandler: FormatHandler<PptxModel> = {
  id: 'odp',
  formatIds: ['odp'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const parsed = read(source.bytes, PREVIEW_SHAPES);
    const slides = toPptxSlides(parsed.slides);

    return {
      kind: 'slides',
      unitCount: slides.length,
      unitKey: 'viewer.slide',
      firstContent: { slide: slides[0] ?? null, width: parsed.width, height: parsed.height },
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<PptxModel>> {
    throwIfAborted(ctx.signal);
    const maxShapes = Math.max(PREVIEW_SHAPES, Math.floor(ctx.limits.maxSpreadsheetCells / 64));
    const parsed = read(source.bytes, maxShapes, ctx.signal);
    if (parsed.slides.length === 0) {
      throw new HandlerError('error.damagedOrIncomplete', 'the presentation declares no slides');
    }

    const { images, skipped } = readImages(parsed.doc, parsed.imageParts, ctx.limits.maxFileBytes / 4);

    const hasMacros = parsed.doc.pkg.directory.entries.some((e) =>
      e.name.startsWith('Basic/') || e.name.startsWith('Scripts/'));
    const hasAnimations = parsed.doc.contentXml.includes('<anim:')
      || parsed.doc.contentXml.includes('<presentation:animations');
    const hasCharts = parsed.doc.pkg.directory.entries.some((e) => e.name.includes('Object')
      && parsed.doc.manifest.some((m) => m.path.startsWith(e.name) && m.mediaType.includes('chart')))
      || parsed.doc.contentXml.includes('draw:object');
    const hasEmbeddedMedia = parsed.doc.contentXml.includes('draw:plugin')
      || parsed.doc.contentXml.includes('presentation:play')
      || parsed.doc.pkg.directory.entries.some((e) => /\.(mp4|mov|avi|wav|mp3|m4a)$/i.test(e.name));

    const spec = FORMATS_BY_ID.get(source.formatId);
    const warnings: HandlerWarning[] = [];

    if (parsed.doc.mimetype && parsed.doc.mimetype !== ODF_MIME['odp']) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'caution',
        detail: `the package declares ${parsed.doc.mimetype}, which is not an OpenDocument presentation`,
      });
    }
    if (hasAnimations) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'info',
        detail: 'Animations and slide transitions are preserved on save but not played (§17).',
      });
    }
    if (hasCharts) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'info',
        detail: 'Charts and embedded objects are preserved on save but shown as a placeholder frame.',
      });
    }
    if (hasMacros) {
      warnings.push({
        messageKey: 'security.macrosPresent',
        severity: 'caution',
        detail: 'This presentation carries Basic macros. They are never executed and are preserved unchanged on save (§30).',
      });
    }
    if (parsed.externalRefs.length > 0) {
      warnings.push({
        messageKey: 'security.externalContent',
        severity: 'caution',
        detail: `${parsed.externalRefs.length} pictures are linked from outside the file and were not fetched (§29).`,
      });
    }
    if (skipped.length > 0) {
      warnings.push({
        messageKey: 'error.featuresUnsupported',
        severity: 'degraded',
        detail: `${skipped.length} pictures could not be decoded for display; they are preserved on save.`,
      });
    }
    if (parsed.truncated) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'degraded',
        detail: 'some slides carry more shapes than this device renders; the remainder are preserved on save',
      });
    }
    if (source.sizeBytes > 20 * 1024 * 1024) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'info',
        detail: `large presentation (${formatFileSize('en', source.sizeBytes)})`,
      });
    }

    return {
      formatId: source.formatId,
      model: {
        slides: toPptxSlides(parsed.slides),
        width: parsed.width,
        height: parsed.height,
        edits: new Map(),
        images,
        hasMacros,
        hasAnimations,
        hasEmbeddedMedia,
        hasCharts,
      },
      readOnly: ctx.assessment.forceReadOnly,
      editLevel: spec?.edit ?? 'basic-edit',
      lossOnSave: spec?.lossOnSave ?? [],
      warnings,
    };
  },

  async exportBytes(
    model: PptxModel, source: FileSource, ctx: HandlerContext,
  ): Promise<ExportResult> {
    throwIfAborted(ctx.signal);
    const doc = openOdf(source.bytes);
    const replacements = new Map<string, Uint8Array>();
    const dropped: string[] = [];

    if (model.edits.size > 0) {
      // Re-parse from the original bytes so the shape spans belong to this XML.
      const styles = parseStyles(doc.stylesXml, doc.contentXml);
      const size = readOdpPageSize(doc.stylesXml);
      const parsed = parseOdp(doc.contentXml, {
        styles, maxShapes: Number.MAX_SAFE_INTEGER, width: size.width, height: size.height,
      });

      let xml = doc.contentXml;
      for (const [part, shapeEdits] of model.edits) {
        const slide = parsed.slides.find((s) => s.part === part);
        if (!slide) throw new HandlerError('error.unableToRead', `no slide for key ${part}`);
        const result = applyOdpShapeEdits(
          xml, slide.shapes,
          [...shapeEdits].map(([id, text]) => ({ id, text })),
        );
        if (result.failed.length > 0) {
          throw new HandlerError(
            'error.unableToRead', `could not rewrite shapes: ${result.failed.join(', ')}`,
          );
        }
        dropped.push(...result.dropped);
        xml = result.xml;
      }
      replacements.set(CONTENT_PART, new TextEncoder().encode(xml));
    }

    const repacked = repackPackage(doc.pkg, replacements);
    return {
      bytes: repacked.bytes,
      suggestedName: source.filename,
      formatId: source.formatId,
      dropped,
    };
  },

  async validateExport(
    bytes: Uint8Array, model: PptxModel, ctx: HandlerContext,
  ): Promise<ValidationReport> {
    throwIfAborted(ctx.signal);
    const issues: string[] = [];
    let reopened = false;
    let contentPreserved = false;

    try {
      const parsed = read(bytes, Number.MAX_SAFE_INTEGER);
      reopened = true;
      issues.push(...checkMimetypeEntry(parsed.doc.pkg, ODF_MIME['odp']!));

      if (parsed.slides.length !== model.slides.length) {
        issues.push(`slide count changed: ${model.slides.length} → ${parsed.slides.length}`);
      }
      for (const [i, expected] of model.slides.entries()) {
        const actual = parsed.slides[i];
        if (!actual) { issues.push(`slide ${i + 1} disappeared`); continue; }
        for (const shape of expected.shapes) {
          if (shape.kind !== 'text') continue;
          const editedText = model.edits.get(expected.part)?.get(shape.id);
          const want = editedText ?? shape.originalText;
          const found = actual.shapes.find((s) => s.id === shape.id);
          if (!found || found.kind !== 'text') {
            issues.push(`slide ${i + 1} shape ${shape.id} disappeared`);
            continue;
          }
          if (found.text !== want) {
            issues.push(`slide ${i + 1} shape ${shape.id}: ${JSON.stringify(want)} → ${JSON.stringify(found.text)}`);
          }
        }
      }
      for (const part of model.images.keys()) {
        if (!hasPart(parsed.doc.pkg, part)) issues.push(`picture part missing: ${part}`);
      }

      contentPreserved = issues.length === 0;
    } catch (err) {
      issues.push(`could not reopen the exported presentation: ${(err as Error).message}`);
    }

    return { ok: reopened && contentPreserved, reopened, contentPreserved, issues };
  },
};

/* ------------------------------------------------------------------ *
 * editing
 * ------------------------------------------------------------------ */

/** Queue a shape's new text. Nothing is written until export (§13). */
export function setOdpShapeText(
  model: PptxModel, slideIndex: number, shapeId: string, text: string,
): PptxModel {
  const slide = model.slides[slideIndex];
  if (!slide) throw new HandlerError('error.unableToRead', `no slide at index ${slideIndex}`);
  const shape = slide.shapes.find((s) => s.id === shapeId);
  if (!shape) throw new HandlerError('error.unableToRead', `no shape ${shapeId} on slide ${slideIndex + 1}`);
  if (shape.kind !== 'text' || !shape.editable) {
    throw new HandlerError(
      'error.featuresUnsupported',
      `shape ${shapeId} contains content that text editing cannot preserve`,
    );
  }

  const edits = new Map(model.edits);
  const forSlide = new Map(edits.get(slide.part) ?? []);
  if (text === shape.originalText) forSlide.delete(shapeId);
  else forSlide.set(shapeId, text);
  if (forSlide.size === 0) edits.delete(slide.part);
  else edits.set(slide.part, forSlide);

  const first = shape.paragraphs[0]?.runs[0];
  const updatedShape = {
    ...shape,
    text,
    paragraphs: text.split('\n').map((line) => ({
      runs: [{
        text: line,
        bold: first?.bold ?? false,
        italic: first?.italic ?? false,
        underline: first?.underline ?? false,
        sizePx: first?.sizePx ?? null,
        color: first?.color ?? null,
        href: null,
      }],
      text: line,
      level: 0,
      alignment: shape.paragraphs[0]?.alignment ?? null,
      bulleted: shape.paragraphs[0]?.bulleted ?? false,
    })),
  };

  const slides = model.slides.map((s, i) => (i === slideIndex ? {
    ...s,
    shapes: s.shapes.map((sh) => (sh.id === shapeId ? updatedShape : sh)),
  } : s));

  return { ...model, slides, edits };
}
